import { ChangeEvent, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';

import { MutationStatusCode, Payment } from '../gql-types.generated';

// The service reason code for a void that arrived after the processor's settlement batch closed.
// It is the one failure a merchant can act on, so it gets its own message.
const alreadySettledReasonCode = 'AlreadySettled';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      width: 420,

      [theme.breakpoints.down('md')]: {
        width: 300,
      },
    },
    reason: {
      marginTop: theme.spacing(2),
    },
  }),
);

interface VoidPaymentProps {
  open: boolean;
  paymentRecord?: Payment;
  voidStatus?: string;
  voidError?: Error;
  voidErrorReasonCode?: string | null;
  sendVoid: (paymentId: string, reason: string) => void;
  clearVoidState: () => void;
  close: () => void;
  submit: () => void;
}

const VoidPayment: React.FC<VoidPaymentProps> = props => {
  const styles = useStyles();
  const { open, paymentRecord, voidStatus, voidError, voidErrorReasonCode, sendVoid, clearVoidState, close, submit } = props;
  const [reason, setReason] = useState('');
  const [inFlight, setInFlight] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setInFlight(false);
    }
  }, [open]);

  // The thunk reports through redux rather than resolving, so completion is observed here.
  useEffect(() => {
    if (!inFlight) return;
    if (voidStatus === MutationStatusCode.Success) {
      setInFlight(false);
      submit();
    } else if (voidStatus === MutationStatusCode.Error || voidError) {
      setInFlight(false);
    }
  }, [voidStatus, voidError, inFlight, submit]);

  const onVoid = () => {
    if (!paymentRecord?.id) return;
    clearVoidState();
    setInFlight(true);
    sendVoid(paymentRecord.id, reason.trim());
  };

  const onClose = () => {
    clearVoidState();
    close();
  };

  const failed = !!voidError || voidStatus === MutationStatusCode.Error;
  const settledInTheGap = voidErrorReasonCode === alreadySettledReasonCode;

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="void-payment-title">
      <DialogTitle id="void-payment-title">Void payment</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Typography variant="body2">
          Voiding cancels this payment at the processor before it settles, so the cardholder is never charged and no refund fee
          applies. This cannot be undone.
        </Typography>

        {failed && (
          <Alert severity={settledInTheGap ? 'warning' : 'error'} data-cy="void-error">
            {settledInTheGap
              ? 'This payment has already settled and can no longer be voided. Issue a refund instead.'
              : voidError?.message || 'The payment could not be voided.'}
          </Alert>
        )}

        <TextField
          className={styles.reason}
          label="Reason"
          fullWidth
          required
          size="small"
          value={reason}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
          inputProps={{ 'data-cy': 'void-reason' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" data-cy="void-cancel">
          Cancel
        </Button>
        <Button
          onClick={onVoid}
          color="primary"
          variant="contained"
          disabled={!reason.trim() || inFlight || settledInTheGap}
          startIcon={inFlight ? <CircularProgress size={16} /> : null}
          data-cy="void-confirm"
        >
          Void payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoidPayment;
